declare global {
  interface Window {
    grecaptcha: {
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      execute: (wigitId?: any) => any;
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      render: (val?: any, data?: any) => any;
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      reset: (wigitId?: any) => any;
    };
  }
}

import { ProductKey as ProductKeys } from '@atlassiansox/bxpkit-core';
import type { AnalyticsTrackType, Product } from '@atlassiansox/bxpkit-core';
import { InjectedIntlProps } from 'react-intl';
import { OriginAnalytics } from './email-field';
import { FirstImpressionsImageUrlsType } from './first-impressions';
import { MigrationDataType } from './utils';
import { SetStateAction } from 'react';
import { OrgInfoResponse } from '../hook/use-org-info';

export { AnalyticsTrackType, ProductKeys, Product };

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class SubmitErrorIDType {
  public static ERROR_TIMEOUT(
    error_id?: string
  ): SetStateAction<SubmitErrorType> {
    let issueIdMessage = error_id
      ? ` (Issue ID for Atlassian use: ${error_id})`
      : '';
    return `<p>Something went wrong on our end. Please try again. If this keeps happening, <a href="https://support.atlassian.com/contact/">contact support</a>.${issueIdMessage}</p>` as SetStateAction<SubmitErrorType>;
  }
}

export enum SubmitErrorType {
  NO_ERROR = '',
  WARNING_TIMEOUT_PENDING = 'warningTimeoutPending',
  ERROR_TIMEOUT = 'errorTimeout',
  AAID_MISMATCH = 'accountIdMisMatch',
  ERROR_ORG_CREATE = 'orgCreateFailure',
  ERROR_PRODUCT_ACTIVATE = 'productActivateFailure'
}

export enum ResendEmailType {
  RESEND_DATA = 'r.e.d'
}

export enum ChooseSignupExperimentVariants {
  VARIANT1 = 'variant_without_login',
  VARIANT2 = 'variant_with_login',
  CONTROL = 'control',
  UNENROLLED = 'unenrolled'
}

export enum ReverseTrialExperimentVariants {
  VARIANT = 'variant',
  CONTROL = 'control',
  UNENROLLED = 'not_enrolled'
}

export type ConsentsObject = {
  key: string;
  displayedText: unknown;
  granted: boolean;
};

export type Consent = {
  site: string | null;
  locale: string | null;
  formUrl: string | null;
  consents: ConsentsObject[];
};

export type WacSignupFormState = {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  password?: string | null;
  cloudName: string | null;
  bbName: string | null;
  dataRegion?: string | null;
  products: Product[];
  consent: Consent;
  isExistingSitePickerN2E: boolean | null;
  isLoggedIn: boolean | null;
  selectedSite: Sites | null;
  migrationId: string | null;
  isSubmitting: boolean;
  isSSOValid: boolean;
  cloudNameLength?: number;
  isNewUser?: boolean | null;
  hasCCPEntitlements?: boolean | null;
  isNewCCPSiteForMigration?: boolean;
};

export type Sites = {
  readonly adminAccess: boolean;
  readonly cloudId: string;
  readonly displayName: string;
  readonly url: string | null;
  readonly products: string[];
  readonly productsAndEditions?: ProductsAndEditions[];
  readonly entitlements?: [] | null;
};

export type ProductsAndEditions = {
  readonly productKey: string;
  readonly productEdition: string;
};

export type UserInfo = {
  readonly firstName: string | undefined;
  readonly fullName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly sites: Sites[] | null;
  readonly isLoggedIn: boolean | false;
  readonly extendedTrialOverridableSites: string[] | null;
  readonly accountId: string | null;
  readonly isNewUser?: boolean | null;
};

export type BitbucketTeam = {
  readonly username: string;
  readonly uuid: string;
};

export type BitbucketWorkspace = {
  // owner: has admin access, collaborator: has write access
  readonly permission: 'owner' | 'member' | 'collaborator';
  readonly user: WorkspaceUserType;
  readonly workspace: WorkspaceType;
  readonly last_accessed: string | null;
  readonly type: string;
  readonly links: Record<string, unknown>;
  readonly added_on: string | null;
};
export type WorkspaceUserType = {
  readonly display_name: string;
  readonly account_id: string;
  readonly links: Record<string, unknown>;
  readonly nickname: string;
  readonly type: string;
  readonly uuid: string;
};

export type WorkspaceType = {
  readonly name: string;
  readonly type: string;
  readonly uuid: string;
  readonly links: Record<string, unknown>;
  readonly slug: string;
};

export type BitbucketWorkspaces = {
  readonly size: number;
  readonly values: BitbucketWorkspace[];
};

export type ProductPayloadObject = {
  readonly accountName?: string | null;
  readonly edition?: string | null;
  readonly product?: string | null;
  readonly productKey?: string | null;
};

export type WacSignupFormProps = {
  readonly productKeys: Product[];
  readonly bitbucketWorkspaces?: BitbucketWorkspaces;
  readonly hideFullName: boolean;
  readonly showEmail: boolean;
  readonly userInfo?: UserInfo;
  readonly marketingConsentRequired: boolean;
  readonly signInAccountUrl: string;
  readonly switchAccountUrl: string;
  readonly originAnalyticsAttributes: OriginAnalytics;
  readonly reCaptchaKey: string;
  readonly devMode: boolean;
  readonly migrationData?: MigrationDataType;
  readonly isStaging: boolean;
  readonly wacEnvUrl: string;
  readonly locale: string;
  readonly templateId?: { templateId: string };
  readonly teamType?: string;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly formExistingSiteShown: (val: boolean) => void;
  readonly deferSiteNameCreation: boolean;
  readonly defaultErrorType?: SubmitErrorType;
  readonly enableFreeBreachTest: boolean;
  readonly simulateFreeBreachState?: boolean;
  readonly spProduct?: string;
  readonly hideGreeting?: boolean;
  readonly outlineOnFocus?: boolean;
  readonly fadeOnDisable?: boolean;
  readonly popOverError?: boolean;
  readonly enableFieldProgression?: boolean;
  readonly showWarningIcon?: boolean;
  readonly selectedSiteId?: string;
  readonly hideCaptchaEula?: boolean;
  readonly enableResendEmail?: boolean;
  readonly enableCarouselEndCard?: boolean;
  readonly firstImpressionsImageUrls?: FirstImpressionsImageUrlsType;
  readonly siteRenamePageUrl: string;
  readonly enableSkipRecaptcha?: boolean;
  readonly allowSignupWithAllSocialOptions?: boolean;
  readonly userOrgInfo?: OrgInfoResponse | null;
  readonly hasCcpEntitlements?: boolean;
  readonly isNewCCPSiteForMigration?: boolean;
};

export type WacSignupFormWithIntlProps = WacSignupFormProps & InjectedIntlProps;

export type HandleSubmitType = {
  readonly payload: WacSignupFormState;
  readonly activateProducts?: boolean;
  readonly cloudId?: string;
  readonly isJswIncluded?: boolean | null;
  readonly gRecaptchaResponse?: string | null;
  readonly productEdition?: string | null;
};

/**
 * userInfo
 */

export type UserSubmitCheckType = {
  readonly accountId: string | null;
};
